import { normalizePhone } from "../../../../supabase/functions/_shared/messaging";
import { formatMoney } from "@szerviz/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { Customer, QuoteChannel, SentMessage } from "../data";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/**
 * Messages to the customer go with channel ticks (owner's rule, 2026-11-04): SMS and e-mail, both ticked by
 * default; SMS only when the "sms" option is on and the customer has a mobile number, e-mail only with an
 * address (otherwise the tick is grey and says why); the program remembers each user's last choice per message
 * type (in this browser); one button sends to every ticked channel.
 */
export type MessageType = "quote" | "ready" | "intake";
export const CHANNELS: readonly QuoteChannel[] = ["email", "sms"];

type Choice = Record<QuoteChannel, boolean>;
const DEFAULT_CHOICE: Choice = { email: true, sms: true };
const storageKey = (type: MessageType, userId: string) => `garagevo.channels.${type}.${userId}`;

export function readChoice(type: MessageType, userId: string | null): Choice {
  if (!userId) return { ...DEFAULT_CHOICE };
  try {
    const v = JSON.parse(globalThis.localStorage?.getItem(storageKey(type, userId)) ?? "null") as Partial<Choice> | null;
    return { email: v?.email !== false, sms: v?.sms !== false };
  } catch {
    return { ...DEFAULT_CHOICE };
  }
}

function saveChoice(type: MessageType, userId: string | null, c: Choice) {
  if (!userId) return;
  try {
    globalThis.localStorage?.setItem(storageKey(type, userId), JSON.stringify(c));
  } catch {
    // not remembered in this browser; the defaults stay
  }
}

/** The customer's number for SMS: the main (mobile) field, or the older second one. */
export const smsNumberOf = (c: Customer | null) => {
  const raw = c?.mobile || c?.phone || "";
  return raw ? normalizePhone(raw) : null;
};

/** Why a channel cannot be used now (an i18n key), or null when it can. */
export function useChannelBlocks(customer: Customer | null, channels: readonly QuoteChannel[] = CHANNELS): Record<QuoteChannel, string | null> {
  const smsOn = useFeature("sms");
  return {
    email: !channels.includes("email") || customer?.email?.trim() ? null : "channels.noEmail",
    sms: !channels.includes("sms") ? null : !smsOn ? "channels.smsOff" : smsNumberOf(customer) ? null : "channels.noMobile",
  };
}

export interface ChannelResult {
  channel: QuoteChannel;
  sent?: SentMessage;
  error?: unknown;
}

/**
 * The ticks and the one send button. `prepare` runs once before sending (e.g. a new link); `send` once per ticked
 * channel. A failed channel does not stop the other one; each result is shown next to the button.
 */
export function ChannelSend({ type, customer, channels = CHANNELS, buttonLabel, prepare, send, onDone, extraMissing = [], primary = true }: {
  type: MessageType;
  customer: Customer | null;
  /** The channels this message knows (the intake link is e-mail only without SMS). */
  channels?: readonly QuoteChannel[];
  buttonLabel: string;
  prepare?: () => Promise<unknown>;
  send: (channel: QuoteChannel, prepared: unknown) => Promise<SentMessage>;
  onDone?: (results: ChannelResult[]) => void | Promise<void>;
  /** Other missing data (shown in the same "still missing" line). */
  extraMissing?: string[];
  primary?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [userId, setUserId] = useState<string | null>(null);
  const [wanted, setWanted] = useState<Choice>(DEFAULT_CHOICE);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ChannelResult[]>([]);
  const blocks = useChannelBlocks(customer, channels);
  useEffect(() => {
    let live = true;
    void store.currentUser().then((u) => {
      if (!live) return;
      setUserId(u?.id ?? null);
      setWanted(readChoice(type, u?.id ?? null));
    });
    return () => {
      live = false;
    };
  }, [store, type]);

  const shown = CHANNELS.filter((c) => channels.includes(c));
  const ticked = shown.filter((c) => wanted[c] && !blocks[c]);
  const noneAvailable = shown.every((c) => blocks[c]);
  const missing = [...(ticked.length === 0 && !noneAvailable ? [t("channels.missing")] : []), ...extraMissing];
  const money = (minor: number) => formatMoney(minor, workshop.currency, i18n.language);

  const go = async () => {
    setBusy(true);
    setResults([]);
    saveChoice(type, userId, wanted);
    const out: ChannelResult[] = [];
    try {
      const prepared = prepare ? await prepare() : undefined;
      for (const c of ticked) {
        try {
          out.push({ channel: c, sent: await send(c, prepared) });
        } catch (error) {
          out.push({ channel: c, error });
        }
      }
    } catch (error) {
      out.push({ channel: ticked[0] ?? "email", error });
    } finally {
      setResults(out);
      setBusy(false);
    }
    await onDone?.(out);
  };

  return (
    <div className="channel-send">
      <fieldset className="channel-ticks">
        <legend>
          {t("channels.legend")} <Help id="channels" />
        </legend>
        {shown.map((c) => {
          const block = blocks[c];
          return (
            <div key={c} className={`channel-tick${block ? " blocked" : ""}`}>
              <label className="consent-channel">
                <input
                  type="checkbox"
                  checked={!block && wanted[c]}
                  disabled={!!block || busy}
                  onChange={(e) => setWanted((w) => ({ ...w, [c]: e.target.checked }))}
                  aria-label={t(`channels.${c}`)}
                />
                <span>{t(`channels.${c}`)}</span>
              </label>
              {block && <small className="muted">{t(block)}</small>}
            </div>
          );
        })}
      </fieldset>
      {noneAvailable && <p className="warn-text">{t("channels.noneAvailable")}</p>}
      <div className="actions">
        <button type="button" className={primary ? "primary" : undefined} disabled={busy || ticked.length === 0 || extraMissing.length > 0} onClick={() => void go()}>
          {busy ? t("quote.sending") : buttonLabel}
        </button>
        {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
      </div>
      {results.length > 0 && (
        <ul className="channel-results" role="status">
          {results.map((r, n) =>
            r.sent ? (
              <li key={n} className="ok">
                {t(r.channel === "email" ? "quote.emailSent" : "quote.smsSent", { to: r.sent.to })}
                {r.sent.price > 0 && ` ${t("sms.priceNote", { price: money(r.sent.price) })}`}
              </li>
            ) : (
              <li key={n}>
                <strong>{t("channels.failedOn", { channel: t(`channels.${r.channel}`) })}</strong>
                <ErrorText error={r.error} />
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
